import React, { useState } from 'react';
import './RespondToSuggestion.css';

// Static, no-backend mockup of a "v4" /respond page — lets us look at the
// look/feel of a "write them a hello message" option before any of it is
// wired up for real. Nothing on this page saves anything; the two buttons
// just flip local state so you can see both the "before" and "after" of
// each interaction. Visit /respond-v4-preview to view it.

const MOCK_COMMUNITY = 'Brighton People';
const MOCK_RECIPIENT_FIRST_NAME = 'James';

const MOCK_PEOPLE = [
  {
    userId: 'mock-1',
    firstName: 'Charlotte',
    age: 29,
    location: 'Sheffield',
    bio: "Into pottery, bad puns, and finding the best flat white in town. New-ish to Brighton and still working out where's good.",
    initials: 'C',
    color: '#f97316',
  },
  {
    userId: 'mock-2',
    firstName: 'Freddie',
    age: 32,
    location: 'Brighton',
    bio: 'Runs a small sound-design studio, plays five-a-side on Tuesdays, always up for a pub quiz team.',
    initials: 'F',
    color: '#0ea5e9',
  },
  {
    userId: 'mock-3',
    firstName: 'Priya',
    age: 27,
    location: 'Hove',
    bio: 'Product designer by day, life drawing on weekends. Looking to meet more people who actually show up to things.',
    initials: 'P',
    color: '#8b5cf6',
  },
];

const HELLO_PLACEHOLDERS = {
  'mock-1': "Hi Charlotte, I saw we're both into pottery — would love to swap notes sometime!",
  'mock-2': 'Hi Freddie, a fellow pub quiz person! Would be great to connect.',
  'mock-3': "Hi Priya, I'm also into life drawing — would love to meet up sometime.",
};

const PersonCard = ({ person }) => {
  const [status, setStatus] = useState(null); // null | 'interested' | 'not_interested' | 'said_hello'
  const [helloOpen, setHelloOpen] = useState(false);
  const [helloText, setHelloText] = useState('');
  const [helloSent, setHelloSent] = useState(false);

  const sendHello = () => {
    if (!helloText.trim()) return;
    setHelloSent(true);
    setStatus('said_hello');
    setHelloOpen(false);
  };

  return (
    <div className="respond-card respond-fade-in">
      <div className="respond-photo-gallery">
        <div className="respond-avatar-mock" style={{ background: person.color }}>{person.initials}</div>
      </div>
      <div className="respond-details">
        <div className="respond-headline">
          <strong>{person.firstName}</strong>
          <span className="respond-age">{person.age}</span>
          <span className="respond-meta"> · {person.location}</span>
        </div>
        {person.bio && <div className="respond-bio">{person.bio}</div>}

        <div className="respond-actions">
          <button
            type="button"
            className={status === 'interested' ? 'button' : 'buttonSecondary'}
            onClick={() => { setStatus('interested'); setHelloOpen(false); }}
          >
            {status === 'interested' ? '✓ Connect' : 'Connect'}
          </button>
          <button
            type="button"
            className={status === 'not_interested' ? 'button' : 'buttonSecondary'}
            onClick={() => { setStatus('not_interested'); setHelloOpen(false); }}
          >
            {status === 'not_interested' ? '✓ Not now' : 'Not now'}
          </button>
        </div>

        {status !== 'said_hello' && (
          <button
            type="button"
            className="respond-v4-hello-link"
            onClick={() => setHelloOpen((v) => !v)}
          >
            {helloOpen ? '— Never mind' : `✎ Or write ${person.firstName} a quick hello instead`}
          </button>
        )}

        {helloOpen && status !== 'said_hello' && (
          <div className="respond-v4-hello-box">
            <textarea
              className="respond-v4-hello-textarea"
              placeholder={HELLO_PLACEHOLDERS[person.userId] || `Say hello to ${person.firstName}...`}
              value={helloText}
              onChange={(e) => setHelloText(e.target.value)}
              rows={3}
            />
            <div className="respond-actions" style={{ marginTop: '0.6rem' }}>
              <button type="button" className="button" onClick={sendHello} disabled={!helloText.trim()}>
                Send hello
              </button>
            </div>
          </div>
        )}

        {status === 'said_hello' && helloSent && (
          <div className="respond-v4-hello-sent">
            <div className="respond-v4-hello-sent-quote">&ldquo;{helloText}&rdquo;</div>
            <div>We'll pass your message on to {person.firstName} and intro you over Messenger 🙂</div>
          </div>
        )}
      </div>
    </div>
  );
};

const RespondV4Preview = () => (
  <div className="respond-page">
    <div className="respond-page-inner">
      <div className="respond-greeting">Hi {MOCK_RECIPIENT_FIRST_NAME}!</div>
      <div className="respond-letter">
        <h1 className="respond-heading">
          Per our email, these people are keen to connect in {MOCK_COMMUNITY}.
        </h1>
        <div className="respond-intro">
          Click Connect if you're keen too — or skip straight to sending them a quick hello.
        </div>
        <div className="respond-intro-sub">
          We won't share your contact details (we intro you over messenger).
        </div>
      </div>

      <div className="respond-people">
        {MOCK_PEOPLE.map((p) => (
          <PersonCard key={p.userId} person={p} />
        ))}
      </div>

      <div className="respond-done respond-fade-in respond-fade-in--slow">
        <button type="button" className="button-fancy" disabled>
          Close window
        </button>
        <div className="respond-done-note">This is a preview — nothing here is saved.</div>
      </div>
    </div>
  </div>
);

export default RespondV4Preview;
